import { useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  Tooltip,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { loginAsUser, logoutUser, parseJwt } from '@realadvisor/auth-client';
import { Image } from '@realadvisor/image';
import {
  ArrowLeftRight,
  CircleUser,
  LogOut,
  SunMoon,
  Undo2,
  UserX,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import type { GetTenantsQuery } from '@graphql/__generated__/graphql';
import { Order_By } from '@graphql/__generated__/graphql';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { GET_TENANTS } from '@modules/select-tenant/selectTenantQueries';
import { useAnalytics } from '@shared/analytics';
import { useLocale } from '@shared/hooks/locale';
import { popoverPaperSx } from '@shared/theme/popoverStyles';
import { cssTokens, customPalette } from '@shared/theme/StyleProvider';
import { getThemeMenuOptions } from '@shared/theme/themeMenuOptions';
import { getTenantSlugStageAndSubdomainFromHostname } from '@shared/utils/tenant';

export const AccountMenu = ({
  sx,
  collapsed = false,
}: {
  sx?: SxProps;
  collapsed?: boolean;
}) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { reset } = useAnalytics();
  const currentTenantName = me?.tenant.name;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [getTenants, { data: tenantsData }] = useLazyQuery(GET_TENANTS);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    getTenants();
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [modal, setModal] = useState<'switchTenant' | 'signInAs' | false>(
    false,
  );

  const { mode, setMode } = useColorScheme();
  // `mode` is undefined on the very first render
  const themeMode = mode ?? 'system';
  const themeOptions = getThemeMenuOptions(t);

  const [loggedInAs, setLoggedInAs] = useState<null | string>(null);

  const tenants = tenantsData?.tenants;

  const imageUrl = me?.user_images?.[0]?.image?.url;

  useEffect(() => {
    const access_token = localStorage.getItem('access_token');
    if (access_token) {
      const { loggedInAs } = parseJwt(access_token);
      setLoggedInAs(loggedInAs);
    }
  }, []);

  return (
    <>
      <ButtonBase
        onClick={handleClick}
        sx={{
          ...sx,
          // left-pinned in both modes (8px padding + 3px margin = the
          // avatar's centered position in the 56px rail) so the avatar
          // does not move while the drawer width animates
          justifyContent: 'flex-start',
          textAlign: 'left',
          // match the nav item labels (fontSize 14 / weight 500)
          fontSize: 14,
          fontWeight: 500,
          p: 1,
          color: 'inherit',
          overflow: 'hidden',
        }}
      >
        <Avatar
          sx={theme => ({
            ml: '3px',
            // 8px padding + 3px margin + 34px avatar + 6px = 51px, the same
            // horizontal offset where the nav item labels start
            mr: collapsed ? 0 : '6px',
            border: `1px solid ${(theme.vars ?? theme).palette.divider}`,
            height: 34,
            width: 34,
          })}
        >
          {imageUrl && (
            <Image
              src={imageUrl ?? undefined}
              options={{
                w: 100,
              }}
            />
          )}
        </Avatar>
        {!collapsed &&
          (me ? [me?.first_name, me?.last_name].join(' ') : t('Loading...'))}
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          // the theme's MuiMenu override pads the list 4px on every side;
          // keep the sides to the items' own 8px margin and give the
          // vertical axis slightly more breathing room
          '& .MuiList-root': {
            py: '9px',
            px: 0,
          },
          '& .MuiMenuItem-root': {
            borderRadius: '8px',
            mx: 1,
            my: '1px',
            fontSize: 14,
            fontWeight: 500,
            minHeight: 36,
          },
          // scope to the items' leading icons so the theme-switch buttons
          // can size their own icons
          '& .MuiSvgIcon-root, & .MuiMenuItem-root > .lucide': {
            width: 20,
            height: 20,
            marginRight: '12px',
            opacity: 0.8,
          },
        }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: { ...popoverPaperSx, minWidth: 220 },
          },
        }}
      >
        <MenuItem
          component={Link}
          to={me?.id != null ? `/users/${me.id}/overview` : ''}
        >
          <CircleUser />
          {t('My account')}
        </MenuItem>
        {(tenants == null || tenants.length > 1) && (
          <MenuItem
            onClick={() => setModal('switchTenant')}
            onMouseDown={() => getTenants()}
          >
            <ArrowLeftRight />
            {currentTenantName ?? t('Change tenant')}
          </MenuItem>
        )}
        {me?.can_user_login_as && me?.is_admin === true && (
          <MenuItem onClick={() => setModal('signInAs')}>
            <UserX />
            {t('Log in as')}
          </MenuItem>
        )}
        {loggedInAs && (
          <MenuItem
            onClick={() => {
              loginAsUser({ user_id: null });
            }}
          >
            <Undo2 />
            {t('Back to my account')}
          </MenuItem>
        )}
        <MenuItem
          disableRipple
          // keep the menu open while switching themes
          onClick={event => event.stopPropagation()}
          sx={{
            cursor: 'default',
            '&:hover': { backgroundColor: 'transparent' },
          }}
        >
          <SunMoon />
          {t('Theme')}
          <Box sx={{ ml: 'auto', pl: 1.5, display: 'flex', gap: '2px' }}>
            {themeOptions.map(({ value, label, Icon }) => (
              <Tooltip key={value} title={label}>
                <IconButton
                  size="small"
                  aria-label={label}
                  onClick={event => {
                    event.stopPropagation();
                    setMode(value);
                  }}
                  sx={{
                    borderRadius: '6px',
                    p: '4px',
                    color:
                      themeMode === value ? 'primary.main' : 'text.secondary',
                    backgroundColor:
                      themeMode === value
                        ? customPalette.blue50
                        : 'transparent',
                    '& .lucide': { width: 16, height: 16 },
                  }}
                >
                  <Icon />
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            reset();
            window.dataLayer = window.dataLayer ?? [];
            window.dataLayer.push({ event: 'RA.Web.Logout' });
            logoutUser();
          }}
        >
          <LogOut />
          {t('Log out')}
        </MenuItem>
      </Menu>
      <ChangeTenant
        currentTenantId={me?.tenant.id}
        tenants={tenants}
        open={modal === 'switchTenant'}
        onClose={() => setModal(false)}
      />
      <SignInAs
        canEditSettings={me?.can_edit_settings ?? false}
        open={modal === 'signInAs'}
        onClose={() => setModal(false)}
      />
    </>
  );
};

type ChangeTenantsProps = {
  currentTenantId?: string;
  tenants?: GetTenantsQuery['tenants'];
  open: boolean;
  onClose: () => void;
};

const ChangeTenant = ({
  currentTenantId,
  tenants,
  open,
  onClose,
}: ChangeTenantsProps) => {
  const { t } = useLocale();

  const getSwitchUrl = (tenantId: string) => {
    const url = new URL(location.href);
    // identify tenant slug
    const { slug, stage } = getTenantSlugStageAndSubdomainFromHostname(
      location.hostname,
    );

    if (!slug) {
      return url.href;
    }

    // replace tenant slug by choosen tenant slug
    if (stage) {
      url.hostname = `${stage}--${
        tenants?.find(t => t.id === tenantId)?.slug ?? ''
      }.${url.hostname.split('.').slice(1).join('.')}`;
    } else {
      url.hostname = url.hostname.replace(
        slug,
        tenants?.find(t => t.id === tenantId)?.slug ?? '',
      );
    }

    return url.href;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.1)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: cssTokens.backdrop, // scheme-aware frosted backdrop
            backdropFilter: 'blur(12px)', // Apply blur effect
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <ArrowLeftRight />
        {t('Chose new tenant')}
      </DialogTitle>
      <DialogContent>
        <Select
          value={currentTenantId}
          onChange={(e: SelectChangeEvent) => {
            const tenantId = e.target.value;
            onClose();
            window.open(getSwitchUrl(tenantId), '_blank');
          }}
          fullWidth
          variant="outlined"
          size="small"
        >
          {tenants?.map(tenant => (
            <MenuItem key={tenant.id} value={tenant.id}>
              {tenant.name}
            </MenuItem>
          ))}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

type SignInAsProps = {
  canEditSettings: boolean;
  open: boolean;
  onClose: () => void;
};

const SignInAs = ({ canEditSettings, open, onClose }: SignInAsProps) => {
  const { t } = useLocale();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.1)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: cssTokens.backdrop, // scheme-aware frosted backdrop
            backdropFilter: 'blur(12px)', // Apply blur effect
          },
        },

        paper: {
          sx: {
            width: '100%',
            maxWidth: 400,
            margin: '0 auto',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <UserX />
        {t('Sign in as')}
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 0 }}>
        <UserSelect
          autoFocus
          onChange={user_id => loginAsUser({ user_id })}
          orderResultsBy={[{ is_broker: Order_By.Desc }]}
          filters={{
            _and: [
              {
                _or: [
                  { is_broker: { _eq: true } },
                  { is_admin: { _eq: true } },
                ],
              },
              ...(canEditSettings
                ? []
                : [
                    {
                      _or: [
                        { can_edit_settings: { _eq: false } },
                        { can_edit_settings: { _is_null: true } },
                      ],
                    },
                  ]),
            ],
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};
